import { Link, useNavigate } from "react-router-dom";
import { Calendar, Eye, Heart, MessageSquare, Users, Video, Mic, BookOpen, MessagesSquare, BookMarked, ScrollText, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";
const FORM_ICONS = {
  lecture: BookOpen,
  presentation: Video,
  discussion: MessagesSquare,
  roundtable: Mic,
};

export default function LectureCard({ lecture }) {
  const Icon = FORM_ICONS[lecture.form] || BookOpen;
  const dt = new Date(lecture.scheduled_at);
  const navigate = useNavigate();
  const goToOwner = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (lecture.owner_username) navigate(`/u/${lecture.owner_username}`);
  };
  return (
    <Link to={`/lectures/${lecture.id}`} className="group et-card flex flex-col overflow-hidden" data-testid={`lecture-card-${lecture.id}`}>
      <div className="aspect-[16/9] bg-slate-100 relative overflow-hidden">
        {lecture.cover_image_url ? (
          <img src={lecture.cover_image_url} alt={lecture.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-navy to-teal flex items-center justify-center">
            <Icon className="h-12 w-12 text-white/80" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className="bg-white/90 text-navy hover:bg-white capitalize">{lecture.form}</Badge>
          {lecture.status === "cancelled" && (
            <Badge className="bg-rose-600 text-white hover:bg-rose-700" data-testid="cancelled-badge">Cancelled</Badge>
          )}
          {lecture.is_live && (
            <Badge className="bg-green-600 text-white hover:bg-green-700 border-2 border-white/40 shadow-md gap-1 animate-pulse" data-testid={`live-badge-${lecture.id}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-white"/>Live now
            </Badge>
          )}
          {lecture.context === "book_release" && (
            <Badge className="bg-rose-600 text-white hover:bg-rose-700 border-2 border-white/40 shadow-md gap-1" data-testid={`context-book-${lecture.id}`}>
              <BookMarked className="h-3 w-3"/>Book release
            </Badge>
          )}
          {lecture.context === "paper_published" && (
            <Badge className="bg-indigo-600 text-white hover:bg-indigo-700 border-2 border-white/40 shadow-md gap-1" data-testid={`context-paper-${lecture.id}`}>
              <ScrollText className="h-3 w-3"/>Paper published
            </Badge>
          )}
          {lecture.with_certificate && (
            <Badge className="bg-amber-500 text-white hover:bg-amber-600 border-2 border-white/40 shadow-md gap-1" data-testid={`certificate-badge-${lecture.id}`}>
              <Award className="h-3 w-3"/>Certificate
            </Badge>
          )}
          {lecture.recorded_url && <Badge className="bg-teal text-white hover:bg-teal-700">Recorded</Badge>}
          {lecture.status !== "cancelled" && lecture.attendee_limit > 0 && (
            (() => {
              // Prefer active_registrations (approved+pending) when present;
              // it's only emitted by the lecture-detail endpoint, so list pages
              // fall back to attendees_count which still counts approved.
              const filled = (lecture.active_registrations ?? lecture.attendees_count) || 0;
              const cap = lecture.attendee_limit;
              const seatsLeft = Math.max(0, cap - filled);
              if (seatsLeft === 0) {
                return <Badge className="bg-amber-600 text-white hover:bg-amber-700" data-testid={`sold-out-badge-${lecture.id}`}>Fully booked</Badge>;
              }
              if (seatsLeft <= Math.max(3, Math.ceil(cap * 0.2))) {
                return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-300" data-testid={`seats-left-badge-${lecture.id}`}>{seatsLeft} seat{seatsLeft === 1 ? "" : "s"} left</Badge>;
              }
              return null;
            })()
          )}
        </div>
        {lecture.fee_amount > 0 && (
          <div className="absolute top-3 right-3 bg-navy text-white text-xs font-semibold px-2.5 py-1 rounded">${lecture.fee_amount}</div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="et-label mb-2">{lecture.field || lecture.topic || "General"}</div>
        <h3 className="font-serif text-lg leading-snug text-navy group-hover:text-teal transition-colors line-clamp-2">{lecture.title}</h3>
        {(lecture.owner_name || lecture.owner_username) && (
          <div className="mt-1.5 text-xs text-slate-600 truncate" data-testid={`lecture-card-host-${lecture.id}`}>
            Hosted by{" "}
            {lecture.owner_username ? (
              <span
                role="link"
                tabIndex={0}
                onClick={goToOwner}
                onKeyDown={(e) => { if (e.key === "Enter") goToOwner(e); }}
                className="font-semibold text-navy cursor-pointer hover:text-teal hover:underline"
                data-testid={`lecture-card-owner-${lecture.id}`}
              >{lecture.owner_name || `@${lecture.owner_username}`}</span>
            ) : (
              <span className="font-semibold text-navy">{lecture.owner_name}</span>
            )}
          </div>
        )}
        <p className="text-sm text-slate-600 mt-2 line-clamp-2">{lecture.description}</p>
        <div className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5"/> {dt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
          <span className="text-[10px] italic text-slate-400">· your local time</span>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-end text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5"/>{lecture.views || 0}</span>
            <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5"/>{lecture.likes || 0}</span>
            <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5"/>{lecture.attendees_count || 0}</span>
            <span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5"/>{lecture.discussions_count || 0}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
